import { useEffect, useState } from 'react';
import type { FC, KeyboardEvent } from 'react';
import { NumberInput, NumberInputField } from '@chakra-ui/react';
import PropTypes from 'prop-types';
import { MinusIcon, Plus2Icon } from '../../../components/icons';
import { Button } from '../../button';
import { MAX_QUANTITY_INPUT_NUMBER } from './const';

export interface QuantityInputProps {
    id: string;
    value: number;
    onChange: (value: number) => void;
    onBlur?: (value: number) => void;
    onKeyDown?: (e: KeyboardEvent<HTMLInputElement>, value: number) => void;
    maxValue?: number;
    strictMaxValue?: boolean;
}

export const QuantityInput: FC<QuantityInputProps> = ({
    onChange,
    onKeyDown,
    value = 1,
    maxValue = 99,
    id,
    onBlur,
    strictMaxValue = false,
}) => {
    const [backupQuantity, setBackupQuantity] = useState<number | null>(value);
    const [quantity, setQuantity] = useState<number>(value);

    useEffect(() => {
        setQuantity(value);
    }, [value]);

    const handleValueChange = (newQuantity: number): void => {
        if (Number.isNaN(newQuantity)) {
            setQuantity(0);
        } else if (newQuantity > 0 && newQuantity <= MAX_QUANTITY_INPUT_NUMBER) {
            const finalQuantity = strictMaxValue ? Math.min(newQuantity, maxValue) : newQuantity;
            setQuantity(finalQuantity);
            setBackupQuantity(finalQuantity);
            onChange(finalQuantity);
        }
    };
    const onClick = (): void => {
        setQuantity(0);
    };
    const handleOnBlur = (): void => {
        if (quantity === 0) {
            setQuantity(backupQuantity ?? value);
        }
        setBackupQuantity(null);
        onBlur?.(quantity);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
        onKeyDown?.(e, quantity);
    };

    const decreaseValue = (): void => handleValueChange(quantity - 1);
    const increaseValue = (): void => handleValueChange(quantity + 1);

    const isOutOfStock = maxValue === 0;

    return (
        <div className="flex">
            <Button
                id="decrement-button"
                onClick={decreaseValue}
                borderRightRadius={0}
                isDisabled={quantity === 1 || isOutOfStock}
                variant="quantity-button"
            >
                <MinusIcon w="12px" h="12px" />
            </Button>
            <NumberInput
                max={99}
                min={0}
                precision={0}
                clampValueOnBlur
                onChange={(valueString) => handleValueChange(parseInt(valueString, 10))}
                onClick={onClick}
                onBlur={handleOnBlur}
                onKeyDown={handleKeyDown}
                value={quantity !== 0 ? quantity : ''}
                isDisabled={isOutOfStock}
            >
                <NumberInputField
                    id={id}
                    data-testid={id}
                    py="6px"
                    px="6px"
                    textAlign="center"
                    borderRadius={0}
                    h={9}
                    w={9}
                    borderColor="slt-pepper.900"
                    _hover={{ borderColor: 'slt-pepper.900' }}
                    _disabled={{
                        opacity: 0.5,
                        borderColor: 'slt-pepper.1000',
                        cursor: 'not-allowed',
                    }}
                    _focusVisible={{ outline: 'none' }}
                    _active={{ outline: 'none' }}
                    className="quantity-input"
                />
            </NumberInput>
            <Button
                id="increment-button"
                onClick={increaseValue}
                borderLeftRadius={0}
                variant="quantity-button"
                isDisabled={quantity >= maxValue || isOutOfStock}
            >
                <Plus2Icon w="12px" h="12px" />
            </Button>
        </div>
    );
};

QuantityInput.propTypes = {
    onChange: PropTypes.func.isRequired,
    value: PropTypes.number.isRequired,
    maxValue: PropTypes.number,
    onBlur: PropTypes.func,
};

QuantityInput.defaultProps = {
    maxValue: 99,
    onKeyDown: undefined,
    onBlur: undefined,
    strictMaxValue: false,
};
